import {
  DndContext,
  DragOverlay,
  PointerSensor,
  closestCenter,
  pointerWithin,
  rectIntersection,
  useSensor,
  useSensors,
  type CollisionDetection,
  type DragEndEvent,
  type DragStartEvent,
} from '@dnd-kit/core';
import { arrayMove } from '@dnd-kit/sortable';
import { type ReactNode, useState } from 'react';

import { DESIGN_CANVAS } from '@/lib/canvas';
import { createRow } from '@/lib/factories';
import { useEditorActions, useEditorStore, useScript } from '@/store/editor.store';
import { pushToast } from '@/store/ui.store';

import { BlockCreator, type PendingInsertion } from './BlockCreator';
import type { DragData, DropData } from './types';

/** One quiz slide = one question (SCHEMA_USAGE.md Rule 4). Reject any mcq
 *  drop on a `kind:"quiz"` slide; tell the teacher to chain instead. */
function rejectMcqOnQuizSlide(targetSlideId: string): boolean {
  const store = useEditorStore.getState();
  if (store.mode !== 'pack') return false;
  const slide = store.script?.slides.find((s) => s.id === targetSlideId);
  if (slide?.beatMeta?.kind !== 'quiz') return false;
  pushToast('Quiz slides hold one question.', {
    kind: 'warning',
    detail: "Use 'Add another question' to chain a new quiz slide.",
  });
  return true;
}

/**
 * Hosts the single @dnd-kit DndContext for the whole editor so that:
 * - toolbar blocks (drag sources) and the canvas (drop targets) share a context
 * - row sortables and toolbar drops coordinate via one event stream
 * - the BlockCreator modal is mounted at provider level (top of the tree)
 */
export function EditorDndProvider({ children }: { children: ReactNode }) {
  const script = useScript();
  const actions = useEditorActions();
  const [activeData, setActiveData] = useState<DragData | null>(null);
  const [pending, setPending] = useState<PendingInsertion | null>(null);

  const sensors = useSensors(
    useSensor(PointerSensor, {
      // Avoid hijacking clicks; user has to drag a few px to start.
      activationConstraint: { distance: 4 },
    }),
  );

  // For toolbar drops we want the droppable directly under the pointer (so a
  // freeform column wins over the surrounding slide / row sortable). For
  // canvas drags (row reorder, element move) closestCenter remains the most
  // forgiving strategy.
  const collisionDetection: CollisionDetection = (args) => {
    const drag = args.active.data.current as DragData | undefined;
    if (drag?.source === 'toolbar') {
      const pointer = pointerWithin(args);
      if (pointer.length > 0) {
        // Prefer column droppables when the pointer is over multiple
        // overlapping containers (e.g. column inside slide).
        const column = pointer.find((c) => {
          const data = (
            args.droppableContainers.find((d) => d.id === c.id)?.data
              .current as DropData | undefined
          )?.kind;
          return data === 'column';
        });
        return column ? [column] : pointer;
      }
      const inter = rectIntersection(args);
      if (inter.length > 0) return inter;
    }
    return closestCenter(args);
  };

  const onDragStart = (e: DragStartEvent) => {
    setActiveData((e.active.data.current as DragData | undefined) ?? null);
  };

  const onDragEnd = (e: DragEndEvent) => {
    const { active, over } = e;
    setActiveData(null);
    if (!script) return;
    const drag = active.data.current as DragData | undefined;
    if (!drag) return;
    const drop = (over?.data.current as DropData | undefined) ?? null;

    // 1) Reordering / moving rows (via @dnd-kit/sortable on the canvas)
    if (drag.source === 'canvas' && drag.kind === 'row') {
      if (!drop) return;
      if (drop?.kind === 'row' && drop.slideId === drag.slideId) {
        const slide = script.slides.find((s) => s.id === drag.slideId);
        if (!slide) return;
        const oldIndex = slide.rows.findIndex((r) => r.id === drag.rowId);
        const newIndex = slide.rows.findIndex((r) => r.id === drop.rowId);
        if (oldIndex < 0 || newIndex < 0 || oldIndex === newIndex) return;
        const ids = arrayMove(
          slide.rows.map((r) => r.id),
          oldIndex,
          newIndex,
        );
        actions.reorderRows(drag.slideId, ids);
        return;
      }
      if (drop?.kind === 'slideBody' && drop.slideId !== drag.slideId) {
        const target = script.slides.find((s) => s.id === drop.slideId);
        if (!target) return;
        actions.moveRow(drag.slideId, drop.slideId, drag.rowId, target.rows.length);
        return;
      }
      if (drop?.kind === 'row' && drop.slideId !== drag.slideId) {
        const target = script.slides.find((s) => s.id === drop.slideId);
        if (!target) return;
        const idx = target.rows.findIndex((r) => r.id === drop.rowId);
        actions.moveRow(drag.slideId, drop.slideId, drag.rowId, Math.max(idx, 0));
        return;
      }
      return;
    }

    // 2) Toolbar block — drops on a column open the widget targeting that
    // column; drops outside any column create a fresh 1-col row at the end of
    // the target slide and open the widget targeting that new row.
    if (drag.source === 'toolbar' && drag.kind === 'element') {
      if (drop?.kind === 'column') {
        // Pack-mode guard: free-floating mcq elements aren't allowed on a
        // quiz slide (would create a "half-ghost quiz" — see Rule 4).
        if (drag.elementType === 'mcq' && rejectMcqOnQuizSlide(drop.slideId)) return;

        const targetRow = script.slides
          .find((s) => s.id === drop.slideId)
          ?.rows.find((r) => r.id === drop.rowId);
        const targetCol = targetRow?.columns.find((c) => c.id === drop.columnId);

        // Every column is a freeform canvas → translate the dropped item's rect
        // into a column-relative (x, y) so the element lands where the user
        // released the pointer. The container is rendered scaled, so convert the
        // screen-space offset into design-space px by comparing the column's
        // rendered width to its design width (canvas width × column% − padding).
        let position: { x: number; y: number; w: number; h: number } | undefined;
        if (targetRow && targetCol && over?.rect && active.rect.current.translated) {
          const overRect = over.rect;
          const activeRect = active.rect.current.translated;
          const designColWidth =
            ((DESIGN_CANVAS.width - targetRow.padding.left - targetRow.padding.right) *
              (targetCol.widthPercent ?? 100)) /
            100;
          const s = overRect.width > 0 && designColWidth > 0 ? overRect.width / designColWidth : 1;
          position = {
            x: Math.max(0, Math.round((activeRect.left - overRect.left) / s)),
            y: Math.max(0, Math.round((activeRect.top - overRect.top) / s)),
            w: 320,
            h: 140,
          };
        }

        setPending({
          elementType: drag.elementType,
          target: { slideId: drop.slideId, rowId: drop.rowId, columnId: drop.columnId },
          position,
        });
        return;
      }

      const sel = useEditorStore.getState().selection;
      const targetSlideId =
        (drop && 'slideId' in drop ? drop.slideId : null) ??
        (sel && 'slideId' in sel ? sel.slideId : null) ??
        script.slides[script.slides.length - 1]?.id ??
        null;
      if (!targetSlideId) return;

      if (drag.elementType === 'mcq' && rejectMcqOnQuizSlide(targetSlideId)) return;

      const newRow = createRow(1);
      actions.insertRow(targetSlideId, newRow);
      setPending({
        elementType: drag.elementType,
        target: { slideId: targetSlideId, rowId: newRow.id, columnId: newRow.columns[0].id },
        createdRow: true,
      });
      return;
    }

    // 3) Existing element dragged to another column
    if (drag.source === 'canvas' && drag.kind === 'element') {
      if (drop?.kind === 'column') {
        actions.moveElement(
          { slideId: drag.slideId, rowId: drag.rowId, columnId: drag.columnId },
          { slideId: drop.slideId, rowId: drop.rowId, columnId: drop.columnId },
          drag.elementId,
          script.slides
            .find((s) => s.id === drop.slideId)
            ?.rows.find((r) => r.id === drop.rowId)
            ?.columns.find((c) => c.id === drop.columnId)?.elements.length ?? 0,
        );
      }
    }
  };

  return (
    <DndContext
      sensors={sensors}
      collisionDetection={collisionDetection}
      onDragStart={onDragStart}
      onDragEnd={onDragEnd}
      onDragCancel={() => setActiveData(null)}
    >
      {children}

      <DragOverlay dropAnimation={null}>
        {activeData?.source === 'toolbar' && activeData.kind === 'element' && (
          <div className="rounded-md border border-border bg-white px-3 py-1.5 text-xs font-medium text-fg shadow-floating">
            + {activeData.elementType}
          </div>
        )}
        {activeData?.source === 'canvas' && activeData.kind === 'row' && (
          <div className="h-12 w-64 rounded-md border-2 border-dashed border-accent/60 bg-accent-soft/40 shadow-floating" />
        )}
      </DragOverlay>

      <BlockCreator
        pending={pending}
        onCancel={() => {
          if (pending?.createdRow) {
            actions.removeRow(pending.target.slideId, pending.target.rowId);
          }
          setPending(null);
        }}
        onConfirm={(element) => {
          if (!pending) return;
          const finalElement = pending.position
            ? { ...element, position: { ...element.position, ...pending.position } }
            : element;
          actions.insertElement(
            pending.target.slideId,
            pending.target.rowId,
            pending.target.columnId,
            finalElement,
            pending.index,
          );
          setPending(null);
        }}
      />
    </DndContext>
  );
}
